import { databaseService } from './DatabaseService';

interface StorageQuota {
  total: number;
  used: number;
  available: number;
  percentage: number;
}

interface StorageAlert {
  level: 'info' | 'warning' | 'critical';
  message: string;
  threshold: number;
  action?: string;
}

class StorageCapacityService {
  private readonly WARNING_THRESHOLD = 0.8; // 80%
  private readonly CRITICAL_THRESHOLD = 0.95; // 95%
  private listeners: ((alert: StorageAlert) => void)[] = [];

  async getStorageQuota(): Promise<StorageQuota> {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await (navigator.storage as any).estimate();
        const total = estimate.quota || 0;
        const used = estimate.usage || 0;
        const available = total - used;
        const percentage = total > 0 ? used / total : 0;

        return { total, used, available, percentage };
      }
    } catch (error) {
      console.warn('Unable to get storage estimate:', error);
    }

    // Fallback for platforms that don't support storage estimation
    return this.getApproximateStorageQuota();
  }

  private async getApproximateStorageQuota(): Promise<StorageQuota> {
    try {
      const stats = await databaseService.getStorageStats();
      
      // Rough approximation based on database size
      const used = stats.usedSize;
      const estimated_total = 100 * 1024 * 1024; // 100MB estimate
      const available = Math.max(0, estimated_total - used);
      const percentage = used / estimated_total;

      return {
        total: estimated_total,
        used,
        available,
        percentage
      };
    } catch (error) {
      console.error('Error getting approximate storage quota:', error);
      return { total: 0, used: 0, available: 0, percentage: 0 };
    }
  }

  async checkStorageHealth(): Promise<StorageAlert[]> {
    const quota = await this.getStorageQuota();
    const alerts: StorageAlert[] = [];

    if (quota.percentage >= this.CRITICAL_THRESHOLD) {
      alerts.push({
        level: 'critical',
        message: 'Storage space critically low! App may not function properly.',
        threshold: this.CRITICAL_THRESHOLD,
        action: 'Clear cache immediately'
      });
    } else if (quota.percentage >= this.WARNING_THRESHOLD) {
      alerts.push({
        level: 'warning',
        message: 'Storage space running low. Consider clearing cache.',
        threshold: this.WARNING_THRESHOLD,
        action: 'Clear old cache data'
      });
    } else {
      alerts.push({
        level: 'info',
        message: 'Storage space is healthy.',
        threshold: quota.percentage
      });
    }

    // Notify listeners of any warnings or critical alerts
    alerts.forEach(alert => {
      if (alert.level !== 'info') {
        this.notifyListeners(alert);
      }
    });

    return alerts;
  }

  async getDetailedStorageBreakdown(): Promise<{
    database: number;
    cache: number;
    attachments: number;
    other: number;
    total: number;
  }> {
    try {
      const stats = await databaseService.getStorageStats();
      const quota = await this.getStorageQuota();

      const database = stats.tableStats.sync_queue?.size || 0 +
                      stats.tableStats.cached_inspection_results?.size || 0 +
                      stats.tableStats.cached_checklist_items?.size || 0;

      const cache = stats.tableStats.cache_metadata?.size || 0 +
                   stats.tableStats.cached_inspectors?.size || 0 +
                   stats.tableStats.cached_sites?.size || 0 +
                   stats.tableStats.cached_groups?.size || 0 +
                   stats.tableStats.cached_expertise_areas?.size || 0 +
                   stats.tableStats.cached_classifications?.size || 0;

      const attachments = stats.tableStats.cached_attachments?.size || 0;

      const other = Math.max(0, quota.used - database - cache - attachments);

      return {
        database,
        cache,
        attachments,
        other,
        total: quota.used
      };
    } catch (error) {
      console.error('Error getting detailed storage breakdown:', error);
      return { database: 0, cache: 0, attachments: 0, other: 0, total: 0 };
    }
  }

  formatStorageSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async optimizeStorage(): Promise<{ freed: number; actions: string[] }> {
    const actions: string[] = [];
    let totalFreed = 0;

    try {
      // Clear expired cache
      const expiredCleared = await databaseService.clearExpiredCache();
      if (expiredCleared > 0) {
        actions.push(`Cleared ${expiredCleared} expired cache entries`);
        totalFreed += expiredCleared * 100; // Rough estimate
      }

      // Clear old sync queue entries
      const oldSyncCleared = await databaseService.clearOldSyncQueue(7);
      if (oldSyncCleared > 0) {
        actions.push(`Cleared ${oldSyncCleared} old sync queue entries`);
        totalFreed += oldSyncCleared * 50; // Rough estimate
      }

      // Vacuum database
      await databaseService.vacuum();
      actions.push('Database optimized');

      // Clear old attachments (older than 30 days)
      const cutoffDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const oldAttachments = await databaseService.delete(
        'cached_attachments',
        'cached_at < ? AND sync_status = ?',
        [cutoffDate.toISOString(), 'completed']
      );
      
      if (oldAttachments > 0) {
        actions.push(`Cleared ${oldAttachments} old attachments`);
        totalFreed += oldAttachments * 1000; // Rough estimate
      }

      return { freed: totalFreed, actions };
    } catch (error) {
      console.error('Error optimizing storage:', error);
      return { freed: 0, actions: ['Error occurred during optimization'] };
    }
  }

  addListener(listener: (alert: StorageAlert) => void): () => void {
    this.listeners.push(listener);
    
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(alert: StorageAlert): void {
    this.listeners.forEach(listener => {
      try {
        listener(alert);
      } catch (error) {
        console.error('Error in storage alert listener:', error);
      }
    });
  }
}

// Export singleton instance
export const storageCapacityService = new StorageCapacityService();
export default storageCapacityService;